import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Card } from "antd";
import firebase from "../../../../configs/FirebaseConfig";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { FIRESTORE_PRODUCT_EVENTS_TABLE } from "../../../../constants/FirestoreConstant";
import { PeriodSelector, ProductEventsTable } from "../../reports/products/components";

const db = firebase.firestore();

const ProductEventsPage = () => {
  const { t } = useTranslation();
  const products = useSelector((state) => state.firestoreReducer.ordered.products);

  const [period, setPeriod] = useState("week");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [productEvents, setProductEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);

  useEffect(() => {
    // initial range: past year
    const startUnix = !!startDate ? dayjs(startDate).unix() : getStartDateForPeriod(period);
    const endUnix = !!endDate ? dayjs(endDate).unix() : dayjs().endOf("day").unix();
    fetchProductEvents(startUnix, endUnix);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, startDate, endDate]);

  const getStartDateForPeriod = (selectedPeriod) => {
    const today = dayjs();
    let start;
    switch (selectedPeriod) {
      case "week":
        start = today.subtract(7, "day");
        break;
      case "month":
        start = today.subtract(1, "month");
        break;
      case "quarter":
        start = today.subtract(3, "month");
        break;
      case "halfyear":
        start = today.subtract(6, "month");
        break;
      case "year":
      default:
        start = today.subtract(1, "year");
    }
    return start.unix();
  };

  const fetchProductEvents = async (startUnix, endUnix) => {
    try {
      setLoadingEvents(true);
      const snap = await db
        .collection(FIRESTORE_PRODUCT_EVENTS_TABLE)
        .where("changedAt", ">=", startUnix)
        .where("changedAt", "<=", endUnix)
        .orderBy("changedAt", "desc")
        .limit(2000)
        .get();

      const items = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

      const enriched = items.map((e) => {
        const p = products?.find((pp) => pp.id === e.productId);
        return {
          ...e,
          productName: p ? `${p.name}` : undefined,
          productCode: p ? `${p.code}` : undefined,
        };
      });

      setProductEvents(enriched);
    } catch (err) {
      console.error("Error fetching product events", err);
      setProductEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  };

  return (
    <Container>
      <Title>{t("product_events")}</Title>

      <Card>
        <TopRow>
          <PeriodSelector
            period={period}
            setPeriod={setPeriod}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
          />
        </TopRow>
      </Card>

      <Card style={{ marginTop: "1rem" }}>
        <ProductEventsTable events={productEvents} loading={loadingEvents} />
      </Card>
    </Container>
  );
};

const Container = styled.div`
  padding: 1rem;
  background-color: #f9fafb;
  min-height: 100vh;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
`;

export default ProductEventsPage;
